(() => {
  if (window.UF_FREE_GIFT_CHECKOUT_INITIALIZED) return;

  window.UF_FREE_GIFT_CHECKOUT_INITIALIZED = true;

  const GIFT_THRESHOLD = 10000;

  const GIFT_PROPERTY_NAME = "FREE GIFT";

  const GIFT_PROPERTY_VALUE =
    "Exclusive Furniture Magnet + Collectible Letter Card by air";

  const PRIVATE_STATE_PROPERTY = "_uf_free_gift_state";


  /**
   * Shopify localized cart endpoint.
   */
  function cartEndpoint(endpoint) {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";

    return `${root}${endpoint}`;
  }


  /**
   * Get the current Shopify cart.
   */
  async function getCart() {
    const response = await fetch(
      cartEndpoint("cart.js"),
      {
        method: "GET",
        headers: {
          Accept: "application/json"
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Unable to load cart: ${response.status}`
      );
    }

    return response.json();
  }


  /**
   * Update one cart line while preserving
   * all existing line item properties.
   */
  async function updateLineProperties(line, properties) {
    const response = await fetch(
      cartEndpoint("cart/change.js"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          id: line.key,
          quantity: line.quantity,
          properties
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        `Unable to update gift property: ${response.status}`
      );
    }

    return response.json();
  }


  /**
   * Synchronize FREE GIFT property before checkout.
   *
   * Eligibility:
   * cart.total_price >= $100
   *
   * The property is attached to only one cart line
   * so Checkout displays the message only once.
   */
  async function syncGiftProperty() {
    const cart = await getCart();

    if (!cart.items || !cart.items.length) {
      return;
    }

    const eligible =
      Number(cart.total_price) >= GIFT_THRESHOLD;


    /**
     * See whether a line already contains
     * our public FREE GIFT property.
     */
    const existingGiftLine = cart.items.find(
      (item) =>
        item.properties &&
        item.properties[GIFT_PROPERTY_NAME]
    );


    /**
     * --------------------------------------------------
     * ELIGIBLE
     * --------------------------------------------------
     */
    if (eligible) {

      /**
       * If gift property already exists,
       * just ensure its value is current.
       */
      if (existingGiftLine) {
        const currentValue =
          existingGiftLine.properties[
            GIFT_PROPERTY_NAME
          ];

        if (currentValue === GIFT_PROPERTY_VALUE) {
          return;
        }

        const properties = {
          ...(existingGiftLine.properties || {})
        };

        properties[GIFT_PROPERTY_NAME] =
          GIFT_PROPERTY_VALUE;

        delete properties[PRIVATE_STATE_PROPERTY];

        await updateLineProperties(
          existingGiftLine,
          properties
        );

        return;
      }


      /**
       * No gift property yet.
       *
       * Attach it to the first cart line.
       */
      const targetLine = cart.items[0];

      const properties = {
        ...(targetLine.properties || {})
      };

      properties[GIFT_PROPERTY_NAME] =
        GIFT_PROPERTY_VALUE;

      delete properties[PRIVATE_STATE_PROPERTY];

      await updateLineProperties(
        targetLine,
        properties
      );

      return;
    }


    /**
     * --------------------------------------------------
     * NOT ELIGIBLE
     * --------------------------------------------------
     *
     * This matters if:
     *
     * 1. Customer qualified for the gift.
     * 2. Entered Checkout.
     * 3. Returned to cart.
     * 4. Removed products.
     * 5. Cart dropped below $100.
     */
    if (existingGiftLine) {
      const properties = {
        ...(existingGiftLine.properties || {})
      };

      delete properties[GIFT_PROPERTY_NAME];


      /**
       * Shopify doesn't allow an existing
       * properties object to be reset to {}.
       *
       * If removing FREE GIFT leaves it empty,
       * retain one private property.
       *
       * Properties beginning with "_" are
       * hidden from customers at checkout.
       */
      if (!Object.keys(properties).length) {
        properties[PRIVATE_STATE_PROPERTY] =
          "inactive";
      }

      await updateLineProperties(
        existingGiftLine,
        properties
      );
    }
  }


  /**
   * Continue to checkout after synchronization.
   */
  function continueToCheckout(button) {

    /**
     * Normal Shopify checkout button.
     */
    if (
      button.form &&
      typeof button.form.requestSubmit === "function"
    ) {
      button.form.requestSubmit(button);
      return;
    }


    /**
     * Checkout link.
     */
    if (
      button.tagName === "A" &&
      button.href
    ) {
      window.location.assign(button.href);
      return;
    }


    /**
     * Safe fallback.
     */
    window.location.assign(
      cartEndpoint("checkout")
    );
  }


  /**
   * Intercept only the checkout button
   * explicitly marked by us.
   */
  document.addEventListener(
    "click",
    async (event) => {

      const button =
        event.target.closest(
          "[data-uf-gift-checkout]"
        );

      if (!button) return;

      if (
        button.dataset.ufGiftProcessing === "true"
      ) {
        return;
      }


      event.preventDefault();
      event.stopImmediatePropagation();

      button.dataset.ufGiftProcessing = "true";

      const originallyDisabled =
        button.disabled === true;

      if ("disabled" in button) {
        button.disabled = true;
      }

      button.setAttribute(
        "aria-busy",
        "true"
      );


      try {

        await syncGiftProperty();

      } catch (error) {

        /**
         * Important:
         *
         * Never prevent a customer from checking out
         * just because the gift message failed.
         */
        console.error(
          "[UnicaFurn] Free gift checkout sync failed:",
          error
        );

      } finally {

        button.removeAttribute("aria-busy");

        if ("disabled" in button) {
          button.disabled = originallyDisabled;
        }

        button.dataset.ufGiftProcessing = "false";
      }


      continueToCheckout(button);
    },
    true
  );
})();